import React, { useState, useRef, useEffect, useCallback } from 'react';
import noUiSlider from 'nouislider';
import 'nouislider/dist/nouislider.css';

// Number of thumbnails to generate
const THUMBNAIL_COUNT = 10;

export function VideoTrimmer({
  videoUrl,
  replayId, // server-side replay session id — trim/upload happen by id
  onTrimComplete,
  onCancel,
  matrixRoomId,
  client
}) {
  const authHeaders = () => {
    const token = client?.getAccessToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  const [videoDuration, setVideoDuration] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [thumbnails, setThumbnails] = useState([]);
  const [isGeneratingThumbnails, setIsGeneratingThumbnails] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const videoRef = useRef(null);
  const sliderRef = useRef(null);
  const sliderInstanceRef = useRef(null);
  const thumbnailVideoRef = useRef(null);

  // Format time as MM:SS or HH:MM:SS
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Generate thumbnails from video
  const generateThumbnails = useCallback(async (url, duration) => {
    setIsGeneratingThumbnails(true);

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.preload = 'metadata';

    const canvas = document.createElement('canvas');
    canvas.width = 160;
    canvas.height = 90;
    const ctx = canvas.getContext('2d');

    const thumbs = [];
    const interval = duration / THUMBNAIL_COUNT;

    try {
      // Load video
      await new Promise((resolve, reject) => {
        video.onloadeddata = resolve;
        video.onerror = reject;
        video.src = url;
      });

      // Generate thumbnails at intervals
      for (let i = 0; i < THUMBNAIL_COUNT; i++) {
        const time = i * interval;

        await new Promise((resolve) => {
          video.currentTime = time;
          video.onseeked = () => {
            try {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              thumbs.push({
                time,
                dataUrl: canvas.toDataURL('image/jpeg', 0.7)
              });
            } catch (e) {
              console.warn('[Trimmer] Failed to capture thumbnail at', time, e);
            }
            resolve();
          };
        });
      }

      setThumbnails(thumbs);
    } catch (e) {
      console.error('[Trimmer] Failed to generate thumbnails:', e);
    } finally {
      video.src = '';
      setIsGeneratingThumbnails(false);
    }
  }, []);

  // Initialize when video loads
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      const duration = video.duration;
      console.log('[Trimmer] Video loaded, duration:', duration);
      setVideoDuration(duration);
      setEndTime(duration);

      // Generate thumbnails
      generateThumbnails(videoUrl, duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      // Stop at end time during preview
      if (video.currentTime >= endTime && isPlaying) {
        video.pause();
        setIsPlaying(false);
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [videoUrl, endTime, isPlaying, generateThumbnails]);

  // Initialize slider when duration is known
  useEffect(() => {
    if (!sliderRef.current || videoDuration <= 0) return;

    // Destroy existing slider
    if (sliderInstanceRef.current) {
      sliderInstanceRef.current.destroy();
    }

    // Create new slider
    const slider = noUiSlider.create(sliderRef.current, {
      start: [0, videoDuration],
      connect: true,
      range: {
        min: 0,
        max: videoDuration
      },
      step: 0.1,
      behaviour: 'tap-drag',
      tooltips: [
        { to: formatTime },
        { to: formatTime }
      ]
    });

    sliderInstanceRef.current = slider;

    slider.on('update', (values) => {
      const start = parseFloat(values[0]);
      const end = parseFloat(values[1]);
      setStartTime(start);
      setEndTime(end);
    });

    slider.on('change', (values) => {
      // Seek video to start when slider changes
      if (videoRef.current) {
        videoRef.current.currentTime = parseFloat(values[0]);
      }
    });

    return () => {
      if (sliderInstanceRef.current) {
        sliderInstanceRef.current.destroy();
        sliderInstanceRef.current = null;
      }
    };
  }, [videoDuration]);

  // Play selection preview
  const playSelection = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = startTime;
      videoRef.current.play();
    }
  };

  // Handle thumbnail click
  const handleThumbnailClick = (time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  // Trim and upload. With a server-side replay session, both steps operate on
  // the server's stored copy — the video bytes never travel through the
  // browser (2026-07-12 rework). Legacy multipart path kept for raw-blob
  // fallback replays (replayId === null).
  const trimAndUpload = async () => {
    setIsProcessing(true);
    setUploadStatus('trimming');

    try {
      console.log('[Trimmer] Trimming video:', startTime, 'to', endTime,
        replayId ? `(server session ${replayId})` : '(legacy blob)');

      let trimmedId = null;
      if (replayId) {
        const trimResponse = await fetch('/api/replay/trim', {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            replay_id: replayId,
            start_time: Number(startTime.toFixed(2)),
            end_time: Number(endTime.toFixed(2)),
          })
        });
        if (!trimResponse.ok) {
          throw new Error('Trim failed: ' + await trimResponse.text());
        }
        const trimMeta = await trimResponse.json();
        trimmedId = trimMeta.id;
        console.log('[Trimmer] Server-side trim done:', trimMeta);
      }

      // Upload to Matrix
      setUploadStatus('uploading');

      let uploadResponse;
      if (trimmedId) {
        uploadResponse = await fetch('/api/replay/upload', {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ replay_id: trimmedId, room_id: matrixRoomId || undefined })
        });
      } else {
        // Legacy: trim + upload by shipping the bytes
        const response = await fetch(videoUrl);
        const blob = await response.blob();
        const formData = new FormData();
        formData.append('video', blob, 'replay.mp4');
        formData.append('start_time', startTime.toFixed(2));
        formData.append('end_time', endTime.toFixed(2));
        const trimResponse = await fetch('/api/replay/trim', {
          method: 'POST',
          headers: authHeaders(),
          body: formData
        });
        if (!trimResponse.ok) {
          throw new Error('Trim failed: ' + await trimResponse.text());
        }
        const trimMeta = await trimResponse.json();
        uploadResponse = await fetch('/api/replay/upload', {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ replay_id: trimMeta.id, room_id: matrixRoomId || undefined })
        });
      }

      if (!uploadResponse.ok) {
        throw new Error('Upload failed: ' + await uploadResponse.text());
      }

      const result = await uploadResponse.json();
      console.log('[Trimmer] Uploaded to Matrix:', result);

      setUploadStatus('success');

      // Close after success
      setTimeout(() => {
        if (onTrimComplete) {
          onTrimComplete(result);
        }
      }, 1500);

    } catch (e) {
      console.error('[Trimmer] Error:', e);
      setUploadStatus('error');
      setTimeout(() => setUploadStatus(null), 3000);
    } finally {
      setIsProcessing(false);
    }
  };

  // Upload without trimming (full video)
  const uploadFull = async () => {
    setIsProcessing(true);
    setUploadStatus('uploading');

    try {
      let uploadResponse;
      if (replayId) {
        // Server already holds the file — upload by id, no byte shuffling.
        uploadResponse = await fetch('/api/replay/upload', {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ replay_id: replayId, room_id: matrixRoomId || undefined })
        });
      } else {
        const response = await fetch(videoUrl);
        const blob = await response.blob();
        const formData = new FormData();
        formData.append('video', blob, `replay-${Date.now()}.mp4`);
        if (matrixRoomId) {
          formData.append('room_id', matrixRoomId);
        }
        uploadResponse = await fetch('/api/replay/upload', {
          method: 'POST',
          headers: authHeaders(),
          body: formData
        });
      }

      if (!uploadResponse.ok) {
        throw new Error('Upload failed: ' + await uploadResponse.text());
      }

      const result = await uploadResponse.json();
      console.log('[Trimmer] Uploaded full video to Matrix:', result);

      setUploadStatus('success');

      setTimeout(() => {
        if (onTrimComplete) {
          onTrimComplete(result);
        }
      }, 1500);

    } catch (e) {
      console.error('[Trimmer] Upload error:', e);
      setUploadStatus('error');
      setTimeout(() => setUploadStatus(null), 3000);
    } finally {
      setIsProcessing(false);
    }
  };

  const trimDuration = endTime - startTime;
  const isTrimmed = startTime > 0.1 || endTime < videoDuration - 0.1;

  return (
    <div className="video-trimmer">
      {/* Video Preview */}
      <div className="trimmer-video-container">
        <video
          ref={videoRef}
          className="trimmer-video"
          src={videoUrl}
          controls
          playsInline
        />
      </div>

      {/* Thumbnail Timeline */}
      <div className="trimmer-timeline">
        {isGeneratingThumbnails ? (
          <div className="timeline-loading">
            <div className="loading-spinner small" />
            <span>Generating preview...</span>
          </div>
        ) : (
          <div className="timeline-thumbnails">
            {thumbnails.map((thumb, index) => (
              <div
                key={index}
                className={`timeline-thumbnail ${
                  thumb.time >= startTime && thumb.time <= endTime ? 'in-range' : 'out-of-range'
                }`}
                onClick={() => handleThumbnailClick(thumb.time)}
                title={formatTime(thumb.time)}
              >
                <img src={thumb.dataUrl} alt={`${formatTime(thumb.time)}`} />
                <span className="thumbnail-time">{formatTime(thumb.time)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Trim Slider */}
      <div className="trimmer-slider-container">
        <div className="trimmer-slider-labels">
          <span className="slider-label start">Start</span>
          <span className="slider-label end">End</span>
        </div>
        <div ref={sliderRef} className="trimmer-slider" />
      </div>

      {/* Time Info */}
      <div className="trimmer-info">
        <div className="time-display">
          <div className="time-item">
            <span className="time-label">Start:</span>
            <span className="time-value">{formatTime(startTime)}</span>
          </div>
          <div className="time-item">
            <span className="time-label">End:</span>
            <span className="time-value">{formatTime(endTime)}</span>
          </div>
          <div className="time-item highlight">
            <span className="time-label">Selection:</span>
            <span className="time-value">{formatTime(trimDuration)}</span>
          </div>
          <div className="time-item">
            <span className="time-label">Total:</span>
            <span className="time-value">{formatTime(videoDuration)}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="trimmer-actions">
        <button
          className="btn btn-secondary"
          onClick={playSelection}
          disabled={isProcessing}
        >
          Preview Selection
        </button>

        <button
          className="btn btn-primary"
          onClick={isTrimmed ? trimAndUpload : uploadFull}
          disabled={isProcessing || uploadStatus === 'success'}
        >
          {uploadStatus === 'trimming' ? 'Trimming...' :
           uploadStatus === 'uploading' ? 'Uploading...' :
           uploadStatus === 'success' ? 'Uploaded!' :
           uploadStatus === 'error' ? 'Failed - Retry' :
           isTrimmed ? 'Trim & Upload' : 'Upload Full Video'}
        </button>

        <button
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={isProcessing}
        >
          Cancel
        </button>
      </div>

      {/* Processing Overlay */}
      {isProcessing && (
        <div className="trimmer-overlay">
          <div className="loading-spinner" />
          <p>{uploadStatus === 'trimming' ? 'Trimming video...' : 'Uploading to Matrix...'}</p>
        </div>
      )}
    </div>
  );
}
